import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./MultiSelectPopup.css";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const extractCollection = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  const possibleArrays = [
    payload?.data,
    payload?.data?.data,
    payload?.data?.results,
    payload?.projects,
    payload?.users,
    payload?.roles,
    payload?.results,
    payload?.records,
    payload?.items,
    payload?.docs,
  ];

  return possibleArrays.find(Array.isArray) || [];
};

const getOptionId = (option) => {
  if (!option) return "";

  if (typeof option === "string" || typeof option === "number") {
    return String(option).trim();
  }

  return String(
    option?._id ?? option?.id ?? option?.value ?? option?.projectId ?? "",
  ).trim();
};

const getOptionLabel = (option, apiTitle = "name") => {
  if (!option) return "Unnamed";

  if (typeof option === "string" || typeof option === "number") {
    return String(option);
  }

  return String(
    option?.[apiTitle] ??
      option?.projectName ??
      option?.name ??
      option?.title ??
      option?.label ??
      option?.projectId ??
      option?._id ??
      "Unnamed",
  ).trim();
};

const normalizeSelectedValues = (values) => {
  if (!Array.isArray(values)) {
    return [];
  }

  return Array.from(
    new Set(values.map((value) => getOptionId(value)).filter(Boolean)),
  );
};

const getSearchableText = (option, apiTitle) => {
  if (typeof option === "string" || typeof option === "number") {
    return String(option).toLowerCase();
  }

  const primitiveValues = Object.values(option || {}).filter(
    (value) =>
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean",
  );

  return [
    getOptionId(option),
    getOptionLabel(option, apiTitle),
    option?.projectId,
    option?.projectName,
    option?.company,
    option?.siteLocation,
    option?.status,
    ...primitiveValues,
  ]
    .filter((value) => value !== undefined && value !== null)
    .join(" ")
    .toLowerCase();
};

/* -------------------------------------------------------------------------- */
/*                              Main Component                                */
/* -------------------------------------------------------------------------- */

const MultiSelectApiPopup = ({
  api,
  apiTitle = "name",
  title = "Select Options",
  searchPlaceholder = "Search options...",

  /*
   * Options supplied by EditFormComponent/FieldRenderer.
   * These are preferred over another API request.
   */
  options: providedOptions = [],

  selectedValues = [],
  onClose,
  onChangeLive,
}) => {
  const [fallbackOptions, setFallbackOptions] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  /*
   * Local selection state ensures that checkbox changes appear
   * immediately even before the parent finishes re-rendering.
   */
  const [selectedIds, setSelectedIds] = useState(() =>
    normalizeSelectedValues(selectedValues),
  );

  const suppliedOptions = useMemo(
    () => extractCollection(providedOptions),
    [providedOptions],
  );

  /*
   * Keep local selected IDs synchronized with formData.
   */
  useEffect(() => {
    setSelectedIds(normalizeSelectedValues(selectedValues));
  }, [selectedValues]);

  /*
   * Close using Escape and prevent the page behind the popup
   * from scrolling.
   */
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  /*
   * Only request the API directly when the parent did not supply
   * options. This request uses the global Axios instance, so your
   * rotating-key interceptor appends:
   *
   * /projects:<key>
   */
  useEffect(() => {
    if (suppliedOptions.length > 0 || !api) {
      setError("");
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const loadFallbackOptions = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await axios.get(api, {
          signal: controller.signal,
        });

        if (controller.signal.aborted) {
          return;
        }

        const records = extractCollection(response.data);

        setFallbackOptions(records);

        if (!records.length) {
          console.warn(
            `No multiselect records returned from ${api}`,
            response.data,
          );
        }
      } catch (requestError) {
        const wasCancelled =
          requestError?.code === "ERR_CANCELED" ||
          requestError?.name === "CanceledError" ||
          controller.signal.aborted;

        if (wasCancelled) {
          return;
        }

        console.error(
          `Failed to load multiselect options from ${api}:`,
          requestError?.response?.data || requestError?.message || requestError,
        );

        setFallbackOptions([]);

        setError(
          requestError?.response?.data?.message ||
            requestError?.response?.data?.error ||
            "Unable to load options.",
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadFallbackOptions();

    return () => controller.abort();
  }, [api, suppliedOptions]);

  /*
   * Parent-provided options have priority.
   */
  const availableOptions = useMemo(
    () => (suppliedOptions.length > 0 ? suppliedOptions : fallbackOptions),
    [suppliedOptions, fallbackOptions],
  );

  /*
   * Remove records without an identifier and duplicate records.
   */
  const uniqueOptions = useMemo(() => {
    const seenIds = new Set();

    return availableOptions.filter((option) => {
      const optionId = getOptionId(option);

      if (!optionId) {
        console.warn("Multiselect option has no usable ID:", option);

        return false;
      }

      if (seenIds.has(optionId)) {
        return false;
      }

      seenIds.add(optionId);
      return true;
    });
  }, [availableOptions]);

  const filteredOptions = useMemo(() => {
    const query = String(searchTerm || "")
      .trim()
      .toLowerCase();

    if (!query) {
      return uniqueOptions;
    }

    return uniqueOptions.filter((option) =>
      getSearchableText(option, apiTitle).includes(query),
    );
  }, [apiTitle, searchTerm, uniqueOptions]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const publishSelection = useCallback(
    (nextIds) => {
      const normalized = normalizeSelectedValues(nextIds);

      setSelectedIds(normalized);
      onChangeLive?.(normalized);
    },
    [onChangeLive],
  );

  const handleToggle = useCallback(
    (option) => {
      const optionId = getOptionId(option);

      if (!optionId) return;

      const nextIds = selectedSet.has(optionId)
        ? selectedIds.filter((selectedId) => selectedId !== optionId)
        : [...selectedIds, optionId];

      publishSelection(nextIds);
    },
    [publishSelection, selectedIds, selectedSet],
  );

  const handleSelectAllVisible = useCallback(() => {
    const visibleIds = filteredOptions.map(getOptionId).filter(Boolean);

    publishSelection([...selectedIds, ...visibleIds]);
  }, [filteredOptions, publishSelection, selectedIds]);

  const handleDeselectVisible = useCallback(() => {
    const visibleIdSet = new Set(
      filteredOptions.map(getOptionId).filter(Boolean),
    );

    publishSelection(
      selectedIds.filter((selectedId) => !visibleIdSet.has(selectedId)),
    );
  }, [filteredOptions, publishSelection, selectedIds]);

  const handleClearAll = useCallback(() => {
    publishSelection([]);
  }, [publishSelection]);

  const allVisibleSelected = useMemo(() => {
    if (filteredOptions.length === 0) {
      return false;
    }

    return filteredOptions.every((option) =>
      selectedSet.has(getOptionId(option)),
    );
  }, [filteredOptions, selectedSet]);

  return (
    <div
      className="multi-select-popup-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div
        className="multi-select-popup"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="multi-select-popup-header">
          <div>
            <h3>{title}</h3>

            <p>
              {selectedIds.length} selected
              {uniqueOptions.length > 0 && ` of ${uniqueOptions.length}`}
            </p>
          </div>

          <button
            type="button"
            className="multi-select-popup-close"
            onClick={onClose}
            aria-label="Close popup"
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Search and bulk actions */}
        <div className="multi-select-popup-toolbar">
          <input
            type="search"
            value={searchTerm}
            placeholder={searchPlaceholder}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="form-input"
            autoFocus
          />

          <div className="multi-select-popup-actions">
            <button
              type="button"
              onClick={
                allVisibleSelected
                  ? handleDeselectVisible
                  : handleSelectAllVisible
              }
              disabled={filteredOptions.length === 0}
            >
              {allVisibleSelected ? "Deselect visible" : "Select visible"}
            </button>

            <button
              type="button"
              onClick={handleClearAll}
              disabled={selectedIds.length === 0}
            >
              Clear all
            </button>
          </div>
        </div>

        {/* Options */}
        <div className="multi-select-popup-body">
          {loading && (
            <div className="multi-select-popup-message">Loading options…</div>
          )}

          {!loading && error && (
            <div className="multi-select-popup-error" role="alert">
              {error}
            </div>
          )}

          {!loading && !error && filteredOptions.length === 0 && (
            <div className="multi-select-popup-message">
              {uniqueOptions.length > 0
                ? "No matching options found."
                : "No options are available."}
            </div>
          )}

          {!loading &&
            !error &&
            filteredOptions.map((option) => {
              const optionId = getOptionId(option);

              const label = getOptionLabel(option, apiTitle);

              const checked = selectedSet.has(optionId);

              return (
                <label
                  key={optionId}
                  className={[
                    "multi-select-popup-option",
                    checked ? "is-selected" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleToggle(option)}
                  />

                  <div className="multi-select-popup-option-content">
                    <strong>{label}</strong>

                    {option?.projectId && (
                      <span>Project ID: {option.projectId}</span>
                    )}

                    {option?.siteLocation && <span>{option.siteLocation}</span>}

                    {option?.company && <span>{option.company}</span>}

                    {option?.status && <span>Status: {option.status}</span>}
                  </div>
                </label>
              );
            })}
        </div>

        {/* Footer */}
        <div className="multi-select-popup-footer">
          <span>
            {filteredOptions.length} visible option
            {filteredOptions.length === 1 ? "" : "s"}
          </span>

          <button
            type="button"
            className="btn-editForm-popup-open"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiSelectApiPopup;
